import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Loader2, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { apiFetch } from '@/lib/api';
import { DEFAULT_CAMPAIGN_CHANNEL, type Channel } from '@/lib/campaignChannel';
import { formatTemplateName } from '@/lib/templateName';
import { useApiErrorToast } from '@/hooks/useApiErrorToast';
import { StepFooter, StepHeader } from './StepLayout';

// Mirrors DEFAULT_CAMPAIGN_NAME in backend/src/controllers/campaign/_shared.js. A campaign created
// before this step existed (or by an older client) can still carry it, and it is shown as an EMPTY
// box so the operator names it rather than keeping the placeholder by accident.
const UNTITLED = 'Untitled campaign';

// A courtesy cap well under the server's 255 (the column width), so the field simply stops
// accepting characters rather than letting somebody type a name that gets rejected.
const NAME_MAX = 120;
// Mirrors DESCRIPTION_MAX in backend/src/controllers/campaign/_shared.js.
const DESCRIPTION_MAX = 2000;

export interface CampaignDetails {
  id: string;
  name: string;
  description?: string | null;
}

interface DetailsStepProps {
  workspaceId: string;
  /** Null on /campaigns/new: nothing exists until this step is submitted. */
  campaign: CampaignDetails | null;
  /** The channel a NEW campaign is created with — the Campaigns tab it was started from. */
  channel?: Channel;
  /**
   * "Create campaign" on an approved template: the new campaign starts with that template chosen
   * and, when the template was made from generated media, with that media as its creative.
   */
  fromTemplate?: { id: string; name: string } | null;
  /** After a create or an update, with what the server now holds. */
  onSaved: (campaign: CampaignDetails, created: boolean) => void;
  /** Leaving the wizard from the first step. */
  onCancel: () => void;
}

/**
 * Step 1: name and description.
 *
 * On a new campaign this is where the row is CREATED - the campaign does not exist until somebody
 * has named it, which is what stops abandoned "Untitled campaign" drafts piling up against the plan
 * limit. On an existing draft it saves only what changed.
 */
export default function DetailsStep({
  workspaceId, campaign, channel = DEFAULT_CAMPAIGN_CHANNEL, fromTemplate = null, onSaved, onCancel,
}: DetailsStepProps) {
  const { t } = useTranslation();
  const showError = useApiErrorToast();
  // A campaign started from a template is named after it until somebody says otherwise.
  const initialName = campaign && campaign.name !== UNTITLED
    ? campaign.name
    : (!campaign && fromTemplate ? formatTemplateName(fromTemplate.name) : '');
  const initialDescription = campaign?.description || '';
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);
  const [saving, setSaving] = useState(false);

  // Re-sync when the campaign is replaced under us. Keyed on the values, not the object, so an
  // unrelated refetch cannot discard typing.
  useEffect(() => { setName(initialName); }, [initialName]);
  useEffect(() => { setDescription(initialDescription); }, [initialDescription]);

  const trimmedName = name.trim();

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!trimmedName || saving) return;

    const nextDescription = description.trim();

    setSaving(true);
    try {
      if (!campaign) {
        const { campaign: created } = await apiFetch<{ campaign: CampaignDetails }>(
          `/workspaces/${workspaceId}/campaigns`,
          // `type` is the channel, fixed from here on. Omitting it is what made every campaign 'sms'.
          {
            method: 'POST',
            // `fromTemplateId` rather than the template's choices copied across: the server reads
            // the template, checks it is this workspace's and approved, and fills in the template
            // and its default media itself.
            body: JSON.stringify({
              name: trimmedName,
              description: nextDescription,
              type: channel,
              ...(fromTemplate ? { fromTemplateId: fromTemplate.id } : {}),
            }),
          },
        );
        onSaved(created, true);
        return;
      }

      const patch: Record<string, string> = {};
      if (trimmedName !== campaign.name) patch.name = trimmedName;
      if (nextDescription !== (campaign.description || '')) patch.description = nextDescription;

      if (Object.keys(patch).length > 0) {
        await apiFetch(`/workspaces/${workspaceId}/campaigns/${campaign.id}`, {
          method: 'PATCH',
          body: JSON.stringify(patch),
        });
      }
      onSaved({ id: campaign.id, name: trimmedName, description: nextDescription || null }, false);
    } catch (error) {
      showError(error, campaign ? 'Could not save these details.' : 'Could not create the campaign.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-6">
      <form onSubmit={submit} noValidate>
        <StepHeader step="details" />

        {!campaign && fromTemplate && (
          // Where the next two steps' answers will come from, so finding them filled in is expected.
          <p className="mb-5 flex max-w-2xl items-start gap-2 rounded-lg bg-muted/50 p-3 text-sm text-muted-foreground" dir="auto">
            <MessageSquare className="mt-0.5 h-4 w-4 shrink-0" />
            {t(
              'campaigns.details.from_template',
              'Starting from the WhatsApp template “{{name}}”. It is chosen for you on the next step, along with the media it was made from — you can change either.',
              { name: formatTemplateName(fromTemplate.name) },
            )}
          </p>
        )}

        <div className="max-w-2xl space-y-5">
          <div className="space-y-2">
            <Label htmlFor="campaign-name">
              {t('campaigns.details.name', 'Campaign name')}
              <span className="text-destructive" aria-hidden="true"> *</span>
            </Label>
            <Input
              id="campaign-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('campaigns.details.name_placeholder', 'e.g. Palm Hills — summer villa launch')}
              maxLength={NAME_MAX}
              required
              autoFocus
              autoComplete="off"
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-baseline justify-between gap-2">
              <Label htmlFor="campaign-description">
                {t('campaigns.details.description', 'Description')}
                <span className="ms-1 font-normal text-muted-foreground">
                  ({t('campaigns.details.optional', 'optional')})
                </span>
              </Label>
              <span className="text-xs text-muted-foreground tabular-nums">
                {description.length}/{DESCRIPTION_MAX}
              </span>
            </div>
            <Textarea
              id="campaign-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t(
                'campaigns.details.description_placeholder',
                'What is this campaign for, and who is it aimed at?',
              )}
              maxLength={DESCRIPTION_MAX}
              rows={4}
              dir="auto"
            />
          </div>
        </div>

        <StepFooter
          onBack={onCancel}
          backLabel={campaign ? t('campaigns.step_back_list', 'Back to campaigns') : t('campaigns.step_cancel', 'Cancel')}
          hint={!trimmedName ? t('campaigns.details.name_required', 'Name the campaign to continue.') : undefined}
          primary={(
            <Button type="submit" disabled={!trimmedName || saving} className="gap-2">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {campaign
                ? t('campaigns.step_next', 'Next')
                : t('campaigns.details.create', 'Create & continue')}
              {!saving && <ArrowRight className="h-4 w-4 rtl:rotate-180" />}
            </Button>
          )}
        />
      </form>
    </Card>
  );
}
